import { useLayoutEffect } from 'react';

// Elements marked data-reveal ease in the first time they scroll into view.
// The hiding class goes on <html> before the first paint, and only when motion is
// allowed, so nothing flashes and nothing stays hidden for reduced-motion visitors.
// New elements (more projects, a filtered grid) are picked up as they mount.
export default function useReveal() {
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    if (!('IntersectionObserver' in window)) return undefined;

    const root = document.documentElement;
    root.classList.add('reveal-ready');

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    const observe = (el) => {
      if (el.classList.contains('is-revealed')) return;
      // Already scrolled past (deep link, back button): show it rather than animate behind the reader
      if (el.getBoundingClientRect().bottom < 0) el.classList.add('is-revealed');
      else io.observe(el);
    };

    document.querySelectorAll('[data-reveal]').forEach(observe);

    const mo = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches('[data-reveal]')) observe(node);
        node.querySelectorAll('[data-reveal]').forEach(observe);
      }));
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      root.classList.remove('reveal-ready');
    };
  }, []);
}
